/**
 * Dead letters — notifications the dispatcher gave up on after the attempt cap (8 → `failed`).
 *
 * The list is `failed` rows only, newest first. There is no payload in it and that is deliberate:
 * the message body holds names and the candidate's personal note, and triage needs the template,
 * channel and failure reason, not the words.
 *
 * Retry returns the row to `queued` with a fresh attempt budget for the dispatcher's next pass.
 * It never sends anything itself — the dispatcher's `FOR UPDATE SKIP LOCKED` claim stays the only
 * path into `sending`, so this console cannot become a second, competing delivery path. The write
 * is guarded on `status='failed'`, so a row currently leased to a worker is never touched:
 * queued/sending/sent all 409 and stay exactly as they were. That 409 is the guard holding, not a
 * failure — it means someone or something else already has the row.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { errorCode, fetchDeadLetters, retryDeadLetter } from '@/lib/admin-api';
import { formatTimestamp, type DeadLetter } from '@/lib/wire';
import { PageHeader } from '@/components/AppShell';
import { ConfirmAction } from '@/components/ConfirmAction';
import { EmptyState, ErrorState, ErrorText, Loading } from '@/components/DataState';
import { Pager } from '@/components/Pager';
import { Badge } from '@/components/ui/badge';

export function DeadLetters() {
  const [offset, setOffset] = useState(0);
  const queryClient = useQueryClient();

  const deadLetters = useQuery({
    queryKey: ['dead-letters', offset],
    queryFn: () => fetchDeadLetters(offset),
  });

  return (
    <>
      <PageHeader title="Dead letters">
        Notifications the dispatcher gave up on after eight attempts. Retrying hands a row back to
        the dispatcher &mdash; it does not send anything from here.
      </PageHeader>

      {deadLetters.isPending && <Loading label="Loading dead letters…" />}
      {deadLetters.isError && <ErrorState onRetry={() => deadLetters.refetch()} />}

      {deadLetters.isSuccess &&
        (deadLetters.data.items.length === 0 ? (
          <EmptyState title="Nothing dead-lettered">
            Every notification has been delivered or is still in flight.
          </EmptyState>
        ) : (
          <>
            <ul className="flex flex-col gap-3">
              {deadLetters.data.items.map((item) => (
                <DeadLetterRow
                  key={item.id}
                  item={item}
                  onRetried={() => queryClient.invalidateQueries({ queryKey: ['dead-letters'] })}
                />
              ))}
            </ul>
            <Pager
              offset={offset}
              total={deadLetters.data.total}
              count={deadLetters.data.items.length}
              onOffsetChange={setOffset}
              noun="dead letter"
            />
          </>
        ))}
    </>
  );
}

function DeadLetterRow({ item, onRetried }: { item: DeadLetter; onRetried: () => void }) {
  const [outcome, setOutcome] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const retry = useMutation({
    mutationFn: () => retryDeadLetter(item.id),
    onSuccess: () => {
      setOutcome('Requeued — the dispatcher will pick it up on its next pass.');
      setFailure(null);
      onRetried();
    },
    onError: (caught) => {
      const code = errorCode(caught);
      if (code?.startsWith('not_failed:')) {
        // The status guard bit. Not an error — the row moved on, and the retry correctly
        // refused to touch it.
        setFailure(
          `Already moving — this row is now "${code.slice('not_failed:'.length)}", so it was left alone. Refresh the list.`,
        );
        return;
      }
      setFailure('That didn’t go through. The row is unchanged. Try again in a moment.');
    },
  });

  return (
    <li className="rounded-lg border border-line bg-surface p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-[14px] font-semibold text-ink">{item.template}</h2>
          <p className="mt-1 font-body text-[11.5px] text-ink-70">
            Created {formatTimestamp(item.createdAt)} &middot; {item.attempts} attempts
            {item.provider ? ` · via ${item.provider}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Badge tone="neutral">{item.channel}</Badge>
          <Badge tone="coral">Failed</Badge>
        </div>
      </div>

      {item.failedReason && (
        <p className="mt-2.5 max-w-[76ch] break-words font-mono text-[11.5px] leading-relaxed text-ink-70">
          {item.failedReason}
        </p>
      )}

      {item.referenceRequestId && (
        <p className="mt-1.5 font-mono text-[11px] text-ink-70">
          Reference {item.referenceRequestId}
        </p>
      )}

      {outcome ? (
        <p
          role="status"
          className="mt-3 rounded-md border border-sage-deep/30 bg-sage-tint/50 px-3.5 py-2.5 font-body text-[12.5px] font-medium text-ink"
        >
          {outcome}
        </p>
      ) : (
        <>
          {failure && <ErrorText className="mt-3">{failure}</ErrorText>}
          <div className="mt-3">
            <ConfirmAction
              label="Retry"
              title="Requeue this notification?"
              confirmLabel="Yes — requeue it"
              pending={retry.isPending}
              onConfirm={() => retry.mutate()}
              className="sm:max-w-[34rem]"
            >
              <p>
                The row goes back to <em>queued</em> with a fresh attempt budget and the dispatcher
                claims it normally. If the underlying problem has not been fixed, it will fail again
                and land back here.
              </p>
              <p className="mt-1.5">This is recorded against your staff id.</p>
            </ConfirmAction>
          </div>
        </>
      )}
    </li>
  );
}
